import React, { useEffect, useRef, useState } from "react";
import mapboxgl, { Map as MapboxMap } from "mapbox-gl";
import CircularProgress from "@mui/material/CircularProgress";
import { config } from "@/config";

import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken = config.mapbox.accessToken;

type MapHeatProps = {
  longitude?: number;
  latitude?: number;
  zoom?: number;
  style?: string;
  geojsonData: GeoJSON.FeatureCollection<GeoJSON.Point>;
  weightProperty?: string;
};

function MapHeat({
  longitude = config.map?.longitude as number,
  latitude = config.map?.latitude as number,
  zoom = config.map?.zoom as number,
  style = config.map?.style,
  geojsonData,
  weightProperty = "weight",
}: MapHeatProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (!mapContainerRef.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style,
      center: [longitude, latitude],
      zoom,
    });

    mapRef.current.on("load", () => {
      setMapLoaded(true);

      mapRef.current?.addSource("heatmap-data", {
        type: "geojson",
        data: geojsonData,
      });

      mapRef.current?.addLayer({
        id: "heatmap-layer",
        type: "heatmap",
        source: "heatmap-data",
        maxzoom: 18,
        paint: {
          "heatmap-weight": [
            "interpolate",
            ["linear"],
            ["get", weightProperty],
            0,
            0,
            6,
            1,
          ],
          "heatmap-intensity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            0,
            1,
            18,
            3,
          ],
          "heatmap-color": [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0,
            "rgba(33,102,172,0)",
            0.2,
            "rgb(103,169,207)",
            0.4,
            "rgb(209,229,240)",
            0.6,
            "rgb(253,219,199)",
            0.8,
            "rgb(239,138,98)",
            1,
            "rgb(178,24,43)",
          ],
          "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 5, 18, 30],
          "heatmap-opacity": [
            "interpolate",
            ["linear"],
            ["zoom"],
            0,
            1,
            15,
            0.8,
            18,
            0.3,
          ],
        },
      });

      mapRef.current?.addLayer({
        id: "points-layer",
        type: "circle",
        source: "heatmap-data",
        minzoom: 15,
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 15, 3, 18, 10],
          "circle-color": [
            "interpolate",
            ["linear"],
            ["get", weightProperty],
            0,
            "rgba(33,102,172,0.5)",
            3,
            "rgb(239,138,98)",
            6,
            "rgb(178,24,43)",
          ],
          "circle-stroke-color": "white",
          "circle-stroke-width": 1,
          "circle-opacity": ["interpolate", ["linear"], ["zoom"], 15, 0, 16, 1],
        },
      });
    });

    return () => mapRef.current?.remove();
  }, [longitude, latitude, zoom, geojsonData, weightProperty]);

  return (
    <div
      data-testid="map-heat-container"
      ref={mapContainerRef}
      className="w-full h-full relative"
    >
      {!mapLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
          <CircularProgress className="text-primary" />
        </div>
      )}
    </div>
  );
}

export default React.memo(MapHeat);
